import React, { useState, useEffect, useMemo } from 'react';
import { Card, Table, Button, Space, Typography, Empty, Spin, Switch, Tag, Tooltip } from 'antd';
import { FileOutlined, DownloadOutlined, EyeOutlined, SwapOutlined, HistoryOutlined } from '@ant-design/icons';
import {useFindByDomainAndDomainId} from '@/shared/api/client/file-upload-controller';

interface LoanDocumentsViewProps {
  data?: any;
  formikProps?: any;
}

/**
 * Component to display documents uploaded for a loan application
 * Used in the LOAN_OFFICER approval view
 * Supports filtering between active and replaced documents
 */
const LoanDocumentsView: React.FC<LoanDocumentsViewProps> = ({ data, formikProps }) => {
  const [loading, setLoading] = useState(true);
  const [showReplacedFiles, setShowReplacedFiles] = useState(false);
  const { Title, Text } = Typography;

  // Extract the requestId from the data context
  const requestId = data?.id || '';
  const branch = data?.valueData?.branch || '';
  const loanId = data?.valueData?.loanId || '';


  // Use the file-upload-controller to fetch documents by category
  // The category will be the loan ID to get all documents for this loan
  const { data: documentsResponse, isLoading } = useFindByDomainAndDomainId(
      'LOAN_APPLICATION',
    branch+'.'+loanId,
      // @ts-ignore
    {enabled: !!loanId  }
  );

  // Document data for the table
  const [documents, setDocuments] = useState<any[]>([]);

  // Process documents when they are loaded
  useEffect(() => {
    if (documentsResponse?.data && Array.isArray(documentsResponse.data)) {
      setDocuments(documentsResponse.data);
      setLoading(false);
    } else if (!isLoading) {
      setLoading(false);
    }
  }, [documentsResponse, isLoading]);

  // Filter documents based on showReplacedFiles toggle
  const filteredDocuments = useMemo(() => {
    if (showReplacedFiles) {
      // Show only replaced files
      return documents.filter(doc => doc.isReplaced === true);
    }
    // Show only active (non-replaced) files
    return documents.filter(doc => doc.isReplaced !== true);
  }, [documents, showReplacedFiles]);

  // Count of replaced documents
  const replacedCount = useMemo(() => {
    return documents.filter(doc => doc.isReplaced === true).length;
  }, [documents]);

  // Count of active documents
  const activeCount = useMemo(() => {
    return documents.filter(doc => doc.isReplaced !== true).length;
  }, [documents]);

  // Handle document view
  const handleViewDocument = (fileId: string) => {
    if (fileId) {
      window.open(`/api/files/preview/documents/${fileId}`, '_blank');
    }
  };

  // Handle document download
  const handleDownloadDocument = (fileId: string) => {
    if (fileId) {
      window.open(`/api/files/download/documents/${fileId}`, '_blank');
    }
  };

  // Table columns configuration
  const columns = [
    {
      title: 'Document Type',
      dataIndex: 'category',
      key: 'category',
      render: (text: string, record: any) => {
        return (
          <Space>
            <span>{text?.replace(/_/g, ' ')}</span>
            {record.isReplacement && (
              <Tooltip title={`Replaces previous version${record.replacesFileId ? ` (ID: ${record.replacesFileId.substring(0, 8)}...)` : ''}`}>
                <Tag color="blue" icon={<SwapOutlined />}>Replacement</Tag>
              </Tooltip>
            )}
            {record.isReplaced && (
              <Tooltip title={`Replaced on ${record.replacedAt ? new Date(record.replacedAt).toLocaleString() : 'unknown date'}${record.replacedByUser ? ` by ${record.replacedByUser}` : ''}`}>
                <Tag color="orange" icon={<HistoryOutlined />}>Replaced</Tag>
              </Tooltip>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Filename',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => (
        <span>
          <FileOutlined style={{ marginRight: 8 }} />
          {text}
        </span>
      )
    },
    {
      title: 'Uploaded Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleString()
    },
    ...(showReplacedFiles ? [{
      title: 'Replaced At',
      dataIndex: 'replacedAt',
      key: 'replacedAt',
      render: (date: string) => date ? new Date(date).toLocaleString() : '-'
    }] : []),
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space size="small">
          <Button
            icon={<EyeOutlined />}
            onClick={() => handleViewDocument(record.id)}
            size="small"
          >
            View
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={() => handleDownloadDocument(record.id)}
            size="small"
          >
            Download
          </Button>
        </Space>
      )
    }
  ];

  return (
    <Card 
      title={
        <Space>
          <span>Loan Application Documents</span>
          {!showReplacedFiles && activeCount > 0 && (
            <Tag color="green">{activeCount} Active</Tag>
          )}
          {showReplacedFiles && replacedCount > 0 && (
            <Tag color="orange">{replacedCount} Replaced</Tag>
          )}
        </Space>
      }
      extra={
        replacedCount > 0 && (
          <Space>
            <Text type="secondary">
              {showReplacedFiles ? 'Viewing replaced files' : 'Viewing active files'}
            </Text>
            <Switch
              checked={showReplacedFiles}
              onChange={setShowReplacedFiles}
              checkedChildren={<HistoryOutlined />}
              unCheckedChildren={<FileOutlined />}
            />
            <Text type="secondary" style={{ minWidth: 120 }}>
              {showReplacedFiles ? `${replacedCount} replaced` : `${replacedCount} replaced available`}
            </Text>
          </Space>
        )
      }
      style={{ marginBottom: 16 }}
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
          <Spin size="large" />
        </div>
      ) : filteredDocuments.length > 0 ? (
        <Table 
          dataSource={filteredDocuments} 
          columns={columns} 
          rowKey="id"
          pagination={false}
        />
      ) : (
        <Empty 
          description={
            showReplacedFiles 
              ? "No replaced documents found" 
              : "No documents have been uploaded for this loan application"
          } 
        />
      )}
    </Card>
  );
};

export default LoanDocumentsView;
